import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import AccessBadge from '../../../../shared/components/AccessBadge';
import TruncatedText from '../../../../shared/components/TruncatedText';
import RowOptionsMenu from '../../../../shared/components/RowOptionsMenu';
import { UserAction } from '../../constants';
import type { UserRole } from '../../../../enums/userEnums';
import type { UserRecord, UserSortField, UserSortOrder } from '../../../../types/UserManagementTypes';
import styles from './UsersTable.module.scss';

const ROW_OPTIONS = [
  { id: UserAction.EDIT_USER, label: 'Edit User' },
  { id: UserAction.VIEW_USER_PROFILE, label: 'View User Profile' },
  { id: UserAction.DELETE_USER, label: 'Delete User' },
];

// Column widths feed antd's <colgroup>, which is what keeps the header and body
// tables aligned; Groups is left unsized so it absorbs the slack.
const COLUMN_WIDTH = {
  name: 240,
  title: 240,
  role: 128,
  accessExpiresAt: 160,
  actions: 48,
};

const sortOrderFor = (columnKey: UserSortField, sortBy: UserSortField, sortOrder: UserSortOrder) => (columnKey === sortBy ? sortOrder : null);

export const buildColumns = (
  onAction: (action: UserAction, user: UserRecord, anchor: HTMLElement | null) => void,
  sortBy: UserSortField,
  sortOrder: UserSortOrder,
): ColumnsType<UserRecord> => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: COLUMN_WIDTH.name,
    sorter: true,
    sortOrder: sortOrderFor('name', sortBy, sortOrder),
    ellipsis: { showTitle: false },
    render: (value: UserRecord['name']) => <TruncatedText text={value} />,
  },
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
    width: COLUMN_WIDTH.title,
    sorter: true,
    sortOrder: sortOrderFor('title', sortBy, sortOrder),
    ellipsis: { showTitle: false },
    render: (value: UserRecord['title']) => <TruncatedText text={value} />,
  },
  {
    title: 'Access',
    dataIndex: 'role',
    key: 'role',
    width: COLUMN_WIDTH.role,
    sorter: true,
    sortOrder: sortOrderFor('role', sortBy, sortOrder),
    render: (role: UserRecord['role']) => <AccessBadge role={role.name as UserRole} />,
  },
  {
    title: 'Duration',
    dataIndex: 'accessExpiresAt',
    key: 'accessExpiresAt',
    width: COLUMN_WIDTH.accessExpiresAt,
    sorter: true,
    sortOrder: sortOrderFor('accessExpiresAt', sortBy, sortOrder),
    render: (value: UserRecord['accessExpiresAt'], record) => (
      <span className={record.durationStatus === 'critical' ? styles.durationCritical : undefined}>
        {value ? dayjs(value).format('MMM D, YYYY') : '—'}
      </span>
    ),
  },
  {
    title: 'Groups',
    dataIndex: 'groups',
    key: 'groups',
    // Backend has no sort_by=groups; this column is display-only.
    ellipsis: { showTitle: false },
    render: (groups: UserRecord['groups']) => <TruncatedText text={groups.join(', ')} />,
  },
  {
    title: '',
    key: 'actions',
    width: COLUMN_WIDTH.actions,
    fixed: 'right',
    render: (_, record) => (
      <RowOptionsMenu options={ROW_OPTIONS} header="User Options" onSelect={(actionId, trigger) => onAction(actionId as UserAction, record, trigger)} />
    ),
  },
];
